import React, { useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { Toast } from '../../../components';
import { SpecializationOutput } from '../types';
import { MainStackParamList } from '../../../navigation/MainNavigator';
import { activityOutputsApi } from '../../../services/apiService';

interface OutputsSectionProps {
  outputs: SpecializationOutput[];
  projectId?: number;
  specializationId?: number;
  activityId?: number;
  onAddOutput?: () => void;
  onViewAll?: () => void;
  onRefresh?: () => void;
  titleOverride?: string;
  readOnly?: boolean;
  disabled?: boolean;
}

function OutputCard({ output, onDelete }: { output: SpecializationOutput; onDelete?: () => void }) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<NativeStackNavigationProp<MainStackParamList>>();
  const imageSource = typeof output.image === 'string' ? { uri: output.image } : output.image;

  return (
    <View style={[cardStyles.container, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {output.image ? (
        <Image source={imageSource} style={cardStyles.image} resizeMode="cover" />
      ) : (
        <View style={cardStyles.placeholder}>
          <Ionicons name="folder-outline" size={48} color={colors.textMuted || '#94A3B8'} />
        </View>
      )}
      <View style={cardStyles.titleWrap}>
        <Text style={[cardStyles.title, { color: colors.textPrimary }]} numberOfLines={2}>
          {output.title}
        </Text>
      </View>
      <View style={cardStyles.footerRow}>
        {onDelete && (
          <TouchableOpacity onPress={onDelete}>
            <Ionicons name="trash-outline" size={16} color="#DC2626" />
          </TouchableOpacity>
        )}
        <View style={{ flex: 1 }} />
        <TouchableOpacity
          style={cardStyles.linkRow}
          onPress={() => navigation.navigate('OutputGallery', { id: output.id, name: output.title || String(output.id) })}
        >
          <Ionicons name="chevron-back" size={14} color={colors.textPrimary} />
          <Text style={[cardStyles.linkText, { color: colors.textPrimary }]}>
            {isRTL ? 'عرض التفاصيل' : 'View Details'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

export default function OutputsSection({ outputs, projectId, specializationId, activityId, onAddOutput, onViewAll, onRefresh, titleOverride, readOnly, disabled }: OutputsSectionProps) {
  const { colors } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [localOutputs, setLocalOutputs] = useState(outputs);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  // Sync with parent prop changes
  React.useEffect(() => { setLocalOutputs(outputs); }, [outputs]);

  const showToast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastType(type);
    setToastMsg(msg);
    setToastVisible(true);
  };

  const handleAdd = () => {
    if (onAddOutput) { onAddOutput(); return; }
    if (!projectId && !specializationId) {
      showToast(isRTL ? 'لا يوجد مشروع مرتبط لإضافة مخرج' : 'No linked project to add output', 'error');
      return;
    }
    navigation.navigate('AddOutput', { projectId, specializationId, activityId } as any);
  };

  const handleDelete = (outputId: number) => {
    setDeleteTargetId(outputId);
  };

  const confirmDelete = async () => {
    if (deleteTargetId === null) return;
    try {
      await activityOutputsApi.delete(deleteTargetId);
      setLocalOutputs(prev => prev.filter(o => o.id !== deleteTargetId));
      showToast(isRTL ? 'تم حذف المخرج' : 'Output deleted', 'success');
      onRefresh?.();
    } catch (err) {
      console.log('[OutputsSection] delete failed:', err);
      showToast(isRTL ? 'فشل في الحذف' : 'Delete failed', 'error');
    }
    setDeleteTargetId(null);
  };

  const navigation = useNavigation<NativeStackNavigationProp<MainStackParamList>>();

  const handleViewAll = () => {
    if (onViewAll) { onViewAll(); return; }
    if (localOutputs.length > 0) {
      const first = localOutputs[0];
      navigation.navigate('OutputGallery', { id: first.id, name: first.title || String(first.id) });
    }
  };

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: colors.card, borderColor: colors.cardBorder },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.linkRow} onPress={handleViewAll}>
          <Ionicons name="chevron-back" size={14} color={colors.textPrimary} />
          <Text style={[styles.linkText, { color: colors.textPrimary }]}>
            {isRTL ? 'عرض الكل' : 'View All'}
          </Text>
        </TouchableOpacity>
        {!readOnly && (
          <TouchableOpacity
            style={[styles.addBtn, { borderColor: '#CBD5E1' }, createShadow(0, 1, 3, '#000', 0.1), disabled && { opacity: 0.4 }]}
            onPress={() => !disabled && handleAdd()}
            disabled={disabled}
          >
            <Text style={[styles.addBtnText, { color: colors.textPrimary }]}>
              {isRTL ? 'إضافة مخرج' : 'Add Output'}
            </Text>
            <Ionicons name="add" size={14} color={colors.textPrimary} />
          </TouchableOpacity>
        )}
        <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
          {titleOverride || (isRTL ? 'المخرجات' : 'Outputs')}
        </Text>
      </View>

      {/* Cards */}
      <View style={[styles.cardsRow, { backgroundColor: colors.backgroundAlt || '#F1F5F9' }]}>
        {localOutputs.length === 0 ? (
          <Text style={[styles.emptyText, { color: colors.textMuted }]}>
            {isRTL ? 'لا توجد مخرجات' : 'No outputs'}
          </Text>
        ) : (
          localOutputs.map((output) => (
            <OutputCard key={output.id} output={output} onDelete={readOnly || disabled ? undefined : () => handleDelete(output.id)} />
          ))
        )}
      </View>
      <Modal visible={deleteTargetId !== null} transparent animationType="fade" onRequestClose={() => setDeleteTargetId(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 24, width: 340, alignItems: 'center' }}>
            <Text style={{ fontSize: 16, fontFamily: FONTS.medium, color: colors.textPrimary, marginBottom: 12 }}>
              {isRTL ? 'تأكيد الحذف' : 'Confirm Delete'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: colors.textSecondary, textAlign: 'center', marginBottom: 20 }}>
              {isRTL ? 'هل أنت متأكد من حذف هذا المخرج؟' : 'Are you sure you want to delete this output?'}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, width: '100%' }}>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0', alignItems: 'center' }} onPress={() => setDeleteTargetId(null)}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ flex: 1, paddingVertical: 12, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' }} onPress={confirmDelete}>
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'حذف' : 'Delete'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      <Toast visible={toastVisible} message={toastMsg} type={toastType} onHide={() => { setToastVisible(false); setToastType('info'); }} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    flex: 1,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.1)',
    minHeight: 32,
  },
  addBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  cardsRow: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 20,
    borderRadius: 8,
    padding: 12,
  },
  emptyText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    width: '100%',
    paddingVertical: 20,
  },
});

const cardStyles = StyleSheet.create({
  container: {
    width: 220,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 8,
  },
  image: {
    width: '100%',
    height: 140,
    borderRadius: 8,
  },
  placeholder: {
    width: '100%',
    height: 140,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleWrap: {
    flex: 1,
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  linkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
});
